import Link from "next/link";
import { cn } from "@/lib/cn";

type CommonProps = {
  variant?: "primary" | "secondary" | "ghost";
  size?: "md" | "lg";
  children: React.ReactNode;
  className?: string;
  showCaret?: boolean;
};

const base =
  "inline-flex items-center gap-2 font-semibold rounded transition-colors select-none";
const sizes = {
  md: "px-4 py-2.5 text-[14px]",
  lg: "px-5 py-3 text-[15px]",
};
const variants = {
  primary:
    "bg-brand-green hover:bg-[#009030] text-white",
  secondary:
    "border-[1.5px] border-ink text-ink hover:bg-ink/5 bg-transparent",
  ghost:
    "text-brand-green hover:underline underline-offset-4 decoration-brand-green/50",
};

export function ButtonLink({
  href,
  variant = "primary",
  size = "md",
  showCaret = true,
  className,
  children,
  ...rest
}: CommonProps & { href: string } & Omit<
    React.ComponentProps<typeof Link>,
    "href" | "children" | "className"
  >) {
  return (
    <Link
      href={href}
      className={cn(base, sizes[size], variants[variant], className)}
      {...rest}
    >
      {showCaret && variant !== "ghost" && <span aria-hidden>▸</span>}
      {children}
    </Link>
  );
}

export function Button({
  variant = "primary",
  size = "md",
  showCaret = true,
  className,
  children,
  ...rest
}: CommonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={cn(base, sizes[size], variants[variant], className)}
      {...rest}
    >
      {showCaret && variant !== "ghost" && <span aria-hidden>▸</span>}
      {children}
    </button>
  );
}
